import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function WhyYesjetsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const benefits = [
    { icon: 'ri-shield-check-line', title: 'Safety First', description: 'Our commitment to safety is unwavering. Every aircraft undergoes rigorous maintenance checks, and our pilots are among the most experienced in the industry with thousands of flight hours.' },
    { icon: 'ri-time-line', title: '24/7 Availability', description: "Your schedule is our priority. Our dedicated concierge team is available around the clock to accommodate your travel needs, whether it's a last-minute business trip or a planned vacation." },
    { icon: 'ri-vip-crown-line', title: 'Luxury Experience', description: 'From gourmet catering to personalized cabin configurations, every detail is tailored to your preferences. Experience true luxury with our premium amenities and white-glove service.' },
    { icon: 'ri-global-line', title: 'Global Network', description: 'Access over 5,000 airports worldwide with seamless connections. Our extensive network ensures you can reach even the most remote destinations with ease and comfort.' },
    { icon: 'ri-user-heart-line', title: 'Personalized Service', description: 'Your dedicated flight coordinator learns your preferences and ensures every journey exceeds expectations. From dietary requirements to entertainment choices, we remember every detail.' },
    { icon: 'ri-price-tag-3-line', title: 'Transparent Pricing', description: "No hidden fees or surprise charges. Our all-inclusive pricing model ensures you know exactly what you're paying for, with detailed quotes provided upfront." },
    { icon: 'ri-calendar-check-line', title: 'Flexible Booking', description: 'Plans change, and we understand that. Enjoy flexible booking options with easy modifications and cancellations, giving you peace of mind when you need it most.' },
    { icon: 'ri-award-line', title: 'Industry Recognition', description: 'Consistently rated among the top private aviation companies, with numerous awards for service excellence, safety standards, and customer satisfaction.' },
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/why-yesjets`,
      name: 'Why Choose YESJETS | Safety, Luxury & 24/7 Private Jet Charter',
      url: `${siteUrl}/why-yesjets`,
      description: 'Discover why YESJETS is the top-rated private jet charter company. FAA Part 135 safety, 24/7 concierge, transparent pricing, flexible booking, and 98% client satisfaction.',
      isPartOf: { '@type': 'WebSite', '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Why YESJETS', item: `${siteUrl}/why-yesjets` },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${siteUrl}/why-yesjets#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Why choose YESJETS for private jet charter?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'YESJETS offers unparalleled luxury in private aviation with 24/7 availability, rigorous safety standards, personalized service, and access to a global network of over 5,000 airports. With 15+ years of experience and a 98% client satisfaction rate, we are the premier choice for discerning travelers.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is YESJETS available 24/7?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Our dedicated concierge team is available around the clock, every day of the year, to accommodate last-minute bookings, itinerary changes, and any special requests.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does YESJETS ensure flight safety?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'YESJETS exclusively works with carefully selected FAA Part 135 operators. Every aircraft undergoes rigorous maintenance checks, and all pilots hold thousands of flight hours with impeccable safety records.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does YESJETS offer transparent pricing?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. We provide all-inclusive quotes with no hidden fees or surprise charges. You will know exactly what you are paying for before you confirm your booking.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I modify or cancel my booking?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. YESJETS offers flexible booking options with easy modifications and cancellations, giving you complete peace of mind when plans change.',
          },
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'YESJETS',
      url: siteUrl,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        bestRating: '5',
        worstRating: '1',
        ratingCount: '312',
        reviewCount: '312',
      },
      review: [
        {
          '@type': 'Review',
          author: { '@type': 'Person', name: 'Michael Chen' },
          reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
          reviewBody: 'YESJETS has transformed the way I travel for business. The attention to detail and personalized service is unmatched.',
        },
        {
          '@type': 'Review',
          author: { '@type': 'Person', name: 'Sarah Williams' },
          reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
          reviewBody: 'From booking to landing, every aspect of the experience was flawless. The crew went above and beyond to ensure our comfort.',
        },
        {
          '@type': 'Review',
          author: { '@type': 'Person', name: 'David Martinez' },
          reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
          reviewBody: 'The flexibility and reliability of YESJETS makes them my first choice for private aviation. Simply the best in the industry.',
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Why YESJETS', item: `${siteUrl}/why-yesjets` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Why Choose YESJETS | Safety, Luxury & 24/7 Private Jet Charter"
        description="Discover why YESJETS is the top-rated private jet charter company. FAA Part 135 safety, 24/7 concierge, transparent pricing, flexible booking, and 98% client satisfaction."
        keywords="why YESJETS, private jet safety, 24/7 jet charter, luxury aviation benefits, FAA Part 135 operators"
        canonical="/why-yesjets"
        jsonLd={jsonLd}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[70vh] w-full overflow-hidden mt-16 md:mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/96b01a21ae062e64491ff44b70d43462.jpg"
            alt="Luxury Interior"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        <div className="relative h-full flex items-center justify-center px-6 md:px-8">
          <div className="text-center text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif font-semibold mb-4 md:mb-6">
              THE YESJETS DIFFERENCE
            </h1>
            <div className="w-24 md:w-32 h-1 bg-gold mx-auto mb-5 md:mb-8"></div>
            <p className="text-base md:text-2xl font-sans text-gray-200 leading-relaxed">
              Experience the pinnacle of private aviation with unmatched service, safety, and sophistication
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 md:py-24 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-serif font-semibold text-black mb-4">Why Choose YESJETS</h2>
            <p className="text-base md:text-lg text-gray-600 font-sans max-w-2xl mx-auto">
              We go beyond expectations to deliver an unparalleled private aviation experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex gap-5 md:gap-6 bg-white p-6 md:p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 rounded-full bg-gold/10 border-2 border-gold flex items-center justify-center flex-shrink-0">
                  <i className={`${benefit.icon} text-2xl md:text-3xl text-gold`}></i>
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-serif font-semibold text-black mb-2 md:mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 font-sans leading-relaxed text-sm md:text-base">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-charcoal">
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-serif font-light text-white mb-5 md:mb-6">
            Experience the YESJETS Difference
          </h2>
          <p className="text-base md:text-xl text-gray-400 font-sans mb-8 md:mb-10">
            Join thousands of satisfied clients who trust us for their private aviation needs
          </p>
          <a href="/booking" className="inline-flex items-center gap-3 bg-gold text-black px-8 md:px-10 py-3 md:py-4 rounded-full text-sm md:text-base font-sans font-semibold tracking-wide hover:bg-gold/90 transition-all cursor-pointer whitespace-nowrap">
            BOOK YOUR FLIGHT
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
